import { SearchBrowseParams } from '@aibees/elephant/types';
import { useMemo } from 'react';
import { SearchFilter, SearchFilterType } from '../../..';
import { INNER_SEPARATOR, ITEM_SEPARATOR } from '../config';
import { getItemQueryType, QUERY_TYPE_VISUAL } from '../query-type';

/** Returns only values slice. Use when component only needs values. */
export function useEmployeeByDepartmentValuesValues(
  params: Partial<SearchBrowseParams>,
  index: number,
  field: string,
  items?: SearchFilter[]
): string[] {
  return useMemo(() => {
    if (!items?.length || !field) return [];
    const values = new Array(items.length).fill(undefined);
    const p = params[field];
    const itemValue = p?.split(ITEM_SEPARATOR);
    const segments = itemValue?.[index || 0]?.split(INNER_SEPARATOR);
    if (segments) {
      // Clamp to items.length so the trailing queryType segment doesn't bleed
      // into values[] (would falsely satisfy empty-field / incomplete checks).
      for (let i = 0; i < items.length; i++) {
        const v = segments[i];
        values[i] = v?.length ? v : undefined;
      }
    }
    return values;
  }, [params, index, field, items]);
}

/**
 * Returns each item with `metadata.color` / `metadata.icon` overridden by the
 * row's queryType visual. Lets the dep autocomplete render its selected chip
 * (and any other metadata-driven UI) in the row's inclusion color/icon.
 */
export function useEmployeeByDepartmentItemsWithQueryTypeVisual(
  params: Partial<SearchBrowseParams>,
  index: number,
  field: string,
  items?: SearchFilter[]
): SearchFilter[] {
  return useMemo(() => {
    if (!items?.length || !field) return [];
    const p = params[field];
    const itemValue = p?.split(ITEM_SEPARATOR);
    const queryType = getItemQueryType(itemValue?.[index], items.length);
    const visual = QUERY_TYPE_VISUAL[queryType];
    return items.map((item) => ({
      ...item,
      metadata: item.metadata
        ? {
            ...item.metadata,
            color: visual.color,
            // Only the dep autocomplete child consumes the icon as a chip
            // leading icon. NUMBER_RANGE / TIME_FRAME render `metadata.icon`
            // inside their inputs, which is not in the design.
            ...(item.type === SearchFilterType.CUSTOM_SELECT && {
              icon: visual.icon,
            }),
          }
        : item.metadata,
    }));
  }, [params, index, field, items]);
}
